import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
  Breadcrumbs,
  Link,
  ListSubheader,
} from '@mui/material';
import {
  Menu as MenuIcon,
  SpaceDashboard as DashboardIcon,
  Devices as DevicesIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  ChevronLeft as ChevronLeftIcon,
  ExpandLess,
  ExpandMore,
  AccountCircle,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  Label as LabelIcon,
  GppMaybe as NonTpmIcon,
  SystemUpdate as UpdateIcon,
  CloudSync as DpsEnrollmentIcon,
  Storage as DfoRegistryIcon,
  People as PeopleIcon,
  History as HistoryIcon,
  FileDownload as ExportIcon,
  Handyman as MaintenanceIcon,
} from '@mui/icons-material';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAppSelector } from '@hooks/redux';
import authService from '@services/authService';
import { SkipNavigation } from '@components/common/SkipNavigation';
import { DotNetIcon } from '@components/common/DotNetIcon';

const drawerWidth = 240;

interface MenuItemConfig {
  text: string;
  icon: React.ReactElement;
  path: string;
  children?: MenuItemConfig[];
}

interface MenuSection {
  heading: string;
  items: MenuItemConfig[];
}

const menuSections: MenuSection[] = [
  {
    heading: 'Fleet',
    items: [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
      { text: 'Devices', icon: <DevicesIcon />, path: '/devices' },
      { text: 'Labels', icon: <LabelIcon />, path: '/labels' },
      { text: 'Export', icon: <ExportIcon />, path: '/export' },
    ],
  },
  {
    heading: 'Provisioning',
    items: [
      { text: 'DPS Enrollment', icon: <DpsEnrollmentIcon />, path: '/dps-enrollment' },
      { text: 'DFO Registry', icon: <DfoRegistryIcon />, path: '/dfo-registry' },
      // Shield-with-"?", not a solid shield: these are the devices without a TPM.
      { text: 'Non-TPM Devices', icon: <NonTpmIcon />, path: '/non-tpm-devices' },
    ],
  },
  {
    heading: 'Updates',
    items: [
      { text: 'Client Updates', icon: <UpdateIcon />, path: '/client-updates' },
      { text: '.NET App Updates', icon: <DotNetIcon />, path: '/dotnet-app-updates' },
      { text: 'Maintenance', icon: <MaintenanceIcon />, path: '/maintenance' },
    ],
  },
  {
    heading: 'Administration',
    items: [
      { text: 'User Management', icon: <PeopleIcon />, path: '/users' },
      { text: 'Audit Log', icon: <HistoryIcon />, path: '/audit-logs' },
      { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    ],
  },
];

export const AppLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const user = useAppSelector((state) => state.auth.user);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleMenuToggle = (text: string) => {
    setOpenItems((prev) =>
      prev.includes(text)
        ? prev.filter((item) => item !== text)
        : [...prev, text]
    );
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    navigate('/profile');
    handleUserMenuClose();
  };

  const handleLogout = () => {
    handleUserMenuClose();
    authService.performLogout();
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const getBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter((x) => x);

    return (
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-separator': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
        }}
      >
        <Link
          component={RouterLink}
          underline="hover"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              color: '#FFFFFF',
            },
          }}
          to="/"
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        {pathnames.map((value, index) => {
          const last = index === pathnames.length - 1;
          const to = `/${pathnames.slice(0, index + 1).join('/')}`;
          const formattedValue = value.charAt(0).toUpperCase() + value.slice(1);

          return last ? (
            <Typography
              key={to}
              sx={{
                color: '#FFFFFF',
                fontWeight: 500,
              }}
            >
              {formattedValue}
            </Typography>
          ) : (
            <Link
              component={RouterLink}
              underline="hover"
              to={to}
              key={to}
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  color: '#FFFFFF',
                },
              }}
            >
              {formattedValue}
            </Link>
          );
        })}
      </Breadcrumbs>
    );
  };

  const drawer = (
    <Box>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          minHeight: '64px !important',
        }}
      >
        <Box
          component="img"
          src="/assets/comnovo-logo.svg"
          alt="Comnovo Logo"
          sx={{
            height: 40,
            width: 'auto',
            maxWidth: '180px',
          }}
        />
        {!isMobile && (
          <IconButton onClick={handleDrawerToggle} aria-label="Close navigation drawer">
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      {menuSections.map((section) => (
      <List
        key={section.heading}
        subheader={
          <ListSubheader
            disableSticky
            sx={{
              bgcolor: 'transparent',
              lineHeight: '32px',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            {section.heading}
          </ListSubheader>
        }
      >
        {section.items.map((item) => (
          <React.Fragment key={item.text}>
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                aria-current={location.pathname === item.path ? 'page' : undefined}
                onClick={() => {
                  if (item.children) {
                    handleMenuToggle(item.text);
                  } else {
                    handleMenuClick(item.path);
                  }
                }}
                aria-label={`Navigate to ${item.text}`}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
                {item.children && (
                  openItems.includes(item.text) ? <ExpandLess /> : <ExpandMore />
                )}
              </ListItemButton>
            </ListItem>
            {item.children && (
              <Collapse in={openItems.includes(item.text)} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children.map((child) => (
                    <ListItemButton
                      key={child.text}
                      sx={{ pl: 4 }}
                      selected={location.pathname === child.path}
                      aria-current={location.pathname === child.path ? 'page' : undefined}
                      onClick={() => handleMenuClick(child.path)}
                      aria-label={`Navigate to ${child.text}`}
                    >
                      <ListItemIcon>{child.icon}</ListItemIcon>
                      <ListItemText primary={child.text} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
      ))}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <SkipNavigation
        links={[
          { label: 'Skip to main content', href: '#main-content', target: 'main' },
          { label: 'Skip to navigation', href: '#navigation', target: 'nav' },
        ]}
      />
      <AppBar
        position="fixed"
        sx={(theme) => ({
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          backdropFilter: 'blur(10px)',
        })}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            aria-expanded={mobileOpen}
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }}>
            {getBreadcrumbs()}
          </Box>

          <IconButton
            color="inherit"
            aria-label="show notifications"
            onClick={handleNotificationClick}
          >
            <NotificationsIcon />
          </IconButton>

          <IconButton
            edge="end"
            aria-label="account of current user"
            aria-controls="user-menu"
            aria-haspopup="true"
            onClick={handleUserMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.email?.charAt(0).toUpperCase() || <AccountCircle />}
            </Avatar>
          </IconButton>

          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>

          <Menu
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={handleNotificationClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleNotificationClose}>
              <Typography variant="body2">New device connected</Typography>
            </MenuItem>
            <MenuItem onClick={handleNotificationClose}>
              <Typography variant="body2">System update available</Typography>
            </MenuItem>
            <MenuItem onClick={handleNotificationClose}>
              <Typography variant="body2">Alert: High temperature detected</Typography>
            </MenuItem>
            <MenuItem onClick={handleNotificationClose}>
              <Typography variant="body2">Backup completed successfully</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        id="navigation"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="Main navigation"
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#FFFFFF',
              borderRight: `1px solid ${theme.palette.divider}`,
            },
            // Improve text readability in menu items
            '& .MuiListItemText-primary': {
              // 14px, not 16: "Non-TPM Devices" wraps to two lines at 16px.
              fontSize: '0.875rem',
              fontWeight: 500,
              color: theme.palette.text.primary,
            },
            '& .MuiListItemButton-root': {
              py: 1.5,
              px: 2,
              my: 0.5,
              mx: 1,
              borderRadius: 2,
              position: 'relative',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: '60%',
                width: '3px',
                backgroundColor: theme.palette.primary.main,
                borderRadius: '0 4px 4px 0',
                opacity: 0,
                transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              },
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                transform: 'translateX(4px)',
                '&::before': {
                  opacity: 0.5,
                },
              },
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main,
                color: '#FFFFFF',
                transform: 'translateX(4px)',
                boxShadow: '0 4px 12px rgba(0, 85, 140, 0.2)',
                '&::before': {
                  opacity: 1,
                  backgroundColor: '#FFFFFF',
                },
                '& .MuiListItemIcon-root': {
                  color: '#FFFFFF',
                },
                '& .MuiListItemText-primary': {
                  color: '#FFFFFF',
                  fontWeight: 600,
                },
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  boxShadow: '0 6px 16px rgba(0, 85, 140, 0.3)',
                },
              },
            },
            '& .MuiListItemIcon-root': {
              minWidth: 40,
              color: theme.palette.text.secondary,
              transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            },
            '& .MuiListItemButton-root:hover .MuiListItemIcon-root': {
              transform: 'scale(1.1)',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        id="main-content"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};
